import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/asmrData';
import Breadcrumb from '@/components/feature/Breadcrumb';

const articleImageUrls: Record<string, string> = {
  'ultimate-trigger-guide': 'https://readdy.ai/api/search-image?query=A%20soft%20minimalist%20scene%20with%20warm%20cream%20and%20beige%20tones%2C%20delicate%20textures%20evoking%20ASMR%20sound%20sensations%20like%20tapping%20and%20whispering%2C%20gentle%20abstract%20compositions%20with%20floating%20elements%2C%20dreamy%20atmosphere%2C%20calming%20aesthetic%2C%20fine%20art%20photography%20style%2C%20soft%20focus%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-detail-01&orientation=landscape',
  'deep-sleep-asmr': 'https://readdy.ai/api/search-image?query=A%20serene%20nighttime%20scene%20with%20soft%20moonlight%20and%20gentle%20stars%2C%20warm%20cream%20and%20lavender%20tones%2C%20dreamy%20abstract%20clouds%2C%20peaceful%20and%20calming%20atmosphere%2C%20minimalist%20aesthetic%2C%20fine%20art%20photography%20style%2C%20soft%20focus%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-detail-02&orientation=landscape',
  'meditation-beginners': 'https://readdy.ai/api/search-image?query=A%20minimalist%20meditation%20space%20with%20warm%20amber%20light%2C%20soft%20flowing%20fabric%20textures%2C%20peaceful%20zen%20atmosphere%2C%20calming%20aesthetic%2C%20fine%20art%20photography%20style%2C%20soft%20focus%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-detail-03&orientation=landscape',
  'spa-roleplay-experience': 'https://readdy.ai/api/search-image?query=A%20luxurious%20spa%20treatment%20room%20with%20warm%20candlelight%2C%20soft%20white%20towels%2C%20cream%20and%20beige%20tones%2C%20calming%20ambiance%2C%20minimalist%20aesthetic%2C%20fine%20art%20photography%20style%2C%20soft%20focus%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-detail-04&orientation=landscape',
  'crunchy-mouth-sounds': 'https://readdy.ai/api/search-image?query=A%20beautifully%20arranged%20food%20still%20life%20with%20crispy%20textures%2C%20warm%20amber%20lighting%2C%20cream%20background%2C%20minimalist%20food%20photography%2C%20artistic%20composition%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-detail-05&orientation=landscape',
  'nature-white-noise': 'https://readdy.ai/api/search-image?query=A%20peaceful%20forest%20scene%20with%20soft%20morning%20light%2C%20gentle%20mist%2C%20green%20leaves%2C%20calming%20nature%20atmosphere%2C%20minimalist%20aesthetic%2C%20fine%20art%20photography%20style%2C%20soft%20focus%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-detail-06&orientation=landscape',
  'tapping-heaven': 'https://readdy.ai/api/search-image?query=A%20minimalist%20arrangement%20of%20various%20textured%20objects%20like%20wood%20and%20glass%20on%20a%20warm%20cream%20surface%2C%20delicate%20compositions%2C%20soft%20natural%20lighting%2C%20calming%20aesthetic%2C%20fine%20art%20photography%20style%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-detail-07&orientation=landscape',
  'haircut-roleplay': 'https://readdy.ai/api/search-image?query=A%20vintage%20barbershop%20interior%20with%20warm%20lighting%2C%20leather%20chair%2C%20silver%20scissors%2C%20cream%20and%20brown%20tones%2C%20cozy%20atmosphere%2C%20minimalist%20aesthetic%2C%20fine%20art%20photography%20style%2C%20soft%20focus%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-detail-08&orientation=landscape',
  'office-stress-relief': 'https://readdy.ai/api/search-image?query=A%20minimalist%20home%20office%20desk%20with%20warm%20sunlight%2C%20mechanical%20keyboard%20and%20notebook%2C%20cream%20and%20beige%20tones%2C%20calm%20atmosphere%2C%20clean%20aesthetic%2C%20fine%20art%20photography%20style%2C%20soft%20focus%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-detail-09&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  if (!article) {
    return (
      <div className="pt-24 pb-20 text-center px-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-900">文章未找到</h1>
        <Link to="/" className="mt-4 inline-block text-accent-600 hover:text-accent-700">返回首页</Link>
      </div>
    );
  }

  return (
    <article className="pt-24 pb-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          ...(category ? [{ label: category.name, to: `/category/${category.slug}` }] : []),
          { label: article.title },
        ]} />

        <header className="mt-8 mb-8">
          {category && (
            <Link
              to={`/category/${category.slug}`}
              className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 hover:bg-accent-200 transition-colors duration-200"
            >
              {category.name}
            </Link>
          )}
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.date}</span>
            <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime} 分钟阅读</span>
            {category && (
              <Link to={`/category/${category.slug}`} className="flex items-center gap-1 text-accent-600 hover:text-accent-700">
                <i className={`${category.icon}`}></i> {category.name}
              </Link>
            )}
          </div>
        </header>

        <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden mb-10">
          <img
            src={articleImageUrls[article.slug] || ''}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="prose prose-lg max-w-none">
          <div className="text-foreground-700 leading-relaxed text-base space-y-6">
            {article.content.split('\n').map((line, i) => {
              const trimmed = line.trim();
              if (!trimmed) return <br key={i} />;
              if (trimmed.startsWith('## ')) {
                return <h2 key={i} className="font-heading text-2xl font-bold text-foreground-900 mt-10 mb-4">{trimmed.replace('## ', '')}</h2>;
              }
              if (trimmed.startsWith('### ')) {
                return <h3 key={i} className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">{trimmed.replace('### ', '')}</h3>;
              }
              if (trimmed.startsWith('**Q:')) {
                return <p key={i} className="font-semibold text-foreground-900 mt-6">{trimmed}</p>;
              }
              if (trimmed.startsWith('**A:')) {
                return <p key={i} className="text-foreground-600 mb-4">{trimmed}</p>;
              }
              return <p key={i} className="text-foreground-700 leading-relaxed">{trimmed}</p>;
            })}
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="mt-16 pt-10 border-t border-background-200">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">相关推荐</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-100 rounded-xl p-5 hover:bg-background-100/80 transition-all duration-300"
                >
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                    {ra.title}
                  </h3>
                  <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{ra.excerpt}</p>
                  <span className="text-xs text-foreground-500">{ra.date}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              datePublished: article.date,
              author: { '@type': 'Organization', name: 'asmr1' },
              publisher: { '@type': 'Organization', name: 'asmr1' },
              inLanguage: 'zh-Hans',
              articleSection: category?.name || '',
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://asmr1.com' },
                ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://asmr1.com/category/${category.slug}` }] : []),
                { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
              ],
            }),
          }}
        />
      </div>
    </article>
  );
}